import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { PlaceholderNode } from '../components/common/LexicalDocEditor/nodes/PlaceholderNode';
import { PageBreakNode } from '../components/common/LexicalDocEditor/nodes/PageBreakNode';
import { LayoutContainerNode, LayoutItemNode } from '../components/common/LexicalDocEditor/nodes/LayoutNode';
// Patches ParagraphNode and HeadingNode prototypes to persist __style in JSON.
import '../components/common/LexicalDocEditor/nodes/StyledParagraphNode';
// Patches TableCellNode prototype to persist __borderColor in JSON and exportDOM.
import '../components/common/LexicalDocEditor/nodes/StyledTableCellNode';

// Must mirror LexicalDocEditor/index.jsx EDITOR_NODES.
const NODES = [
  HeadingNode,
  QuoteNode,
  ListNode, ListItemNode,
  TableNode, TableCellNode, TableRowNode,
  LinkNode, AutoLinkNode,
  CodeNode, CodeHighlightNode,
  HorizontalRuleNode,
  PlaceholderNode,
  PageBreakNode,
  LayoutContainerNode, LayoutItemNode,
];

/**
 * Convert a Lexical editor state JSON to an HTML string.
 * Runs synchronously in the browser using a throwaway editor instance.
 */
export function lexicalToHtml(lexicalJson) {
  try {
    const editor = createEditor({ nodes: NODES, onError: () => {} });

    const stateStr = typeof lexicalJson === 'string'
      ? lexicalJson
      : JSON.stringify(lexicalJson);

    const editorState = editor.parseEditorState(stateStr);

    let html = '';
    editorState.read(() => {
      html = $generateHtmlFromNodes(editor, null);
    });

    // Force border-collapse:collapse on every exported <table>.
    html = html.replace(/<table(\s[^>]*)?>/gi, (match, attrs = '') => {
      if (/border-collapse\s*:/i.test(attrs)) return match;
      const styleMatch = attrs.match(/style="([^"]*)"/i);
      if (styleMatch) {
        return match.replace(
          /style="([^"]*)"/i,
          `style="${styleMatch[1]};border-collapse:collapse;border-spacing:0"`
        );
      }
      return `<table${attrs} style="border-collapse:collapse;border-spacing:0">`;
    });

    // Strip default inline border-* styles from td/th so html2canvas doesn't
    // double-draw them at cell intersections (making internal borders look bold
    // in PDF). Preserve any border rule marked !important — those come from
    // StyledTableCellNode.exportDOM() and represent an explicit user choice
    // (e.g. transparent, none, or a custom color).
    html = html.replace(/<(td|th)(\s[^>]*)?>/gi, (match, tag, attrs = '') => {
      if (!attrs || !/border/i.test(attrs)) return match;
      const patched = attrs.replace(/style="([^"]*)"/i, (_, style) => {
        const cleaned = style
          .split(';')
          .filter(r => {
            const rule = r.trim();
            if (!/^\s*border(-\w+)?\s*:/i.test(rule)) return true; // not a border rule — keep
            return /!important/i.test(rule); // keep only explicit !important overrides
          })
          .join(';')
          .replace(/;+/g, ';')
          .replace(/^;|;$/g, '');
        return `style="${cleaned}"`;
      });
      return `<${tag}${patched}>`;
    });

    return html;
  } catch (e) {
    console.error('lexicalToHtml error:', e);
    return '';
  }
}

/**
 * Clean a raw placeholder/slug so it's readable when rendered as fallback text:
 * strips surrounding `{{ }}`, converts `-` and `_` to spaces, collapses whitespace.
 */
const prettifySlug = (raw) => String(raw ?? '')
  .replace(/^\{\{|\}\}$/g, '')
  .replace(/[-_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * Pick the best display string for an unfilled placeholder:
 *   1) admin-set title (label)
 *   2) cleaned `placeholder` pattern from the DB
 *   3) cleaned slug from the token itself
 */
const fallbackTextFor = (slug, placeholderInfo) => {
  const title = placeholderInfo?.title && String(placeholderInfo.title).trim();
  if (title) return title;
  const prettyFromPattern = prettifySlug(placeholderInfo?.placeholder || '');
  if (prettyFromPattern) return prettyFromPattern;
  return prettifySlug(slug);
};

/**
 * Replace {{slug}} tokens (and PlaceholderNode-exported spans) with user values.
 * Filled values → styled span. Empty → unfilled span with a readable fallback
 * (label → cleaned pattern → cleaned slug).
 */
export function replacePlaceholders(baseHtml, values = {}, placeholders = {}) {
  if (!baseHtml) return '';

  // Replace PlaceholderNode exported spans: <span class="doc-placeholder-token" ...>{{x}}</span>
  // The original span may carry an inline style (font-size, color, etc.) set by the admin —
  // extract and forward it so filled values render with the correct typography.
  let html = baseHtml.replace(
    /<span[^>]*class="doc-placeholder-token"[^>]*>[^<]*<\/span>/g,
    (match) => {
      const slugMatch = match.match(/data-slug="([^"]+)"/);
      if (!slugMatch) return match;
      const slug = slugMatch[1];
      const styleMatch = match.match(/style="([^"]*)"/);
      const inlineStyle = styleMatch ? ` style="${styleMatch[1]}"` : '';
      const value = values[slug];
      if (value !== undefined && value !== null && String(value).trim()) {
        return `<span class="doc-value-filled" data-slug="${slug}"${inlineStyle}>${escHtml(String(value))}</span>`;
      }
      return `<span class="doc-value-unfilled" data-slug="${slug}"${inlineStyle}>${escHtml(fallbackTextFor(slug, placeholders[slug]))}</span>`;
    }
  );

  // Fallback: replace any remaining plain {{slug}} tokens
  html = html.replace(/\{\{([^}]+)\}\}/g, (_, rawSlug) => {
    const slug = rawSlug.trim();
    const value = values[slug];
    if (value !== undefined && value !== null && String(value).trim()) {
      return `<span class="doc-value-filled" data-slug="${slug}">${escHtml(String(value))}</span>`;
    }
    return `<span class="doc-value-unfilled" data-slug="${slug}">${escHtml(fallbackTextFor(slug, placeholders[slug]))}</span>`;
  });

  return html;
}

function escHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
